import { ClosableModal } from '@Components/shared/dialogs/ClosableModal';
import { CreateDialogFooterLayout } from '@Components/shared/layouts/CreateDialogFooterLayout';
import { AtLocator } from '@Components/shared/locators/AtLocator';
import { Button, MenuItem, Stack, Typography } from '@mui/material';
import FormProvider, { RHFRadioGroup, RHFSelect, RHFTextField } from '@Components/hook-form';
import { ChangeEvent, useEffect } from 'react';
import { useWatch } from 'react-hook-form';

// Styles
import { useCreateFreebets } from '@Modal/hooks/freebets/useCreateFreebets';
import { useRevokeFreebetsForm } from '@Forms/freebets/RevokeFreebetsFormContext';
import {
  FreeBetAccessStatusEnum,
  usePaginatedFreeBetsCampaignsQuery,
} from '@Src/generated.graphql';

export const RevokeFreebetsModalDialog = () => {
  const {
    showRevokeFreebetsForm,
    handleRevokeFreebetsModal,
    handleSubmitRevokeFreebets,
    isRevokeSubmittable,
  } = useCreateFreebets();
  const methods = useRevokeFreebetsForm();
  const { data: campaignsData, refetch } = usePaginatedFreeBetsCampaignsQuery({
    variables: {
      limit: 100,
      page: 1,
      search: '',
      filter: { status: FreeBetAccessStatusEnum.Active, validCampaigns: true },
    },
  });
  const { setValue, reset, control } = methods;
  const revokeType = useWatch({ control, name: 'revokeType' });
  const file = useWatch({ control, name: 'file' });

  useEffect(() => {
    if (!showRevokeFreebetsForm) {
      return;
    }
    void refetch();
  }, [showRevokeFreebetsForm, refetch]);

  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] ?? null;
    setValue('file', selectedFile, { shouldDirty: true });
    event.target.value = '';
  };

  const handleClose = () => {
    reset();
    handleRevokeFreebetsModal(false);
  };

  return (
    <>
      {showRevokeFreebetsForm && (
        <ClosableModal
          name="RevokeFreebets"
          title="Revoke Freebets"
          open={showRevokeFreebetsForm}
          onClose={handleClose}
          sx={{ width: '592px' }}
        >
          <FormProvider methods={methods}>
            <Stack gap={2} sx={{ py: 2 }}>
              {/* <RHFTextField name="campaignId" label="Campaign ID" /> */}
              <RHFSelect name="campaignId" label="Campaign" type="text">
                {campaignsData?.paginatedFreeBetsCampaigns?.docs?.map((campaign) => (
                  <MenuItem key={campaign._id} value={campaign._id}>
                    {campaign.description}
                  </MenuItem>
                ))}
              </RHFSelect>
              <RHFRadioGroup
                row
                name="revokeType"
                options={[
                  { label: 'Player IDs', value: 'playerIds' },
                  { label: 'Full Revoke', value: 'fullRevoke' },
                  { label: 'Request ID', value: 'requestId' },
                ]}
              />
              {revokeType === 'playerIds' && (
                <Stack gap={1}>
                  <Button
                    component="label"
                    variant="outlined"
                    sx={{ width: '100%', cursor: 'pointer' }}
                  >
                    Choose file
                    <input
                      type="file"
                      accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                      onChange={handleFileUpload}
                      style={{ display: 'none' }}
                    />
                  </Button>
                  {file ? <Typography variant="body2">{file.name}</Typography> : null}
                </Stack>
              )}
              {revokeType === 'requestId' && <RHFTextField name="requestId" label="Request ID" />}
            </Stack>
            <CreateDialogFooterLayout>
              <AtLocator type="button" name="Cancel">
                <Button variant="outlined" color="inherit" onClick={handleClose}>
                  Cancel
                </Button>
              </AtLocator>
              <AtLocator type="button" name="Submit">
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!isRevokeSubmittable}
                  onClick={handleSubmitRevokeFreebets}
                >
                  Confirm
                </Button>
              </AtLocator>
            </CreateDialogFooterLayout>
          </FormProvider>
        </ClosableModal>
      )}
    </>
  );
};
